import { createTheme } from '@mui/material/styles';

// Typography settings
const typography = {
  fontFamily: "'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
  h1: {
    fontWeight: 700,
    fontSize: '2.5rem',
  },
  h2: {
    fontWeight: 700,
    fontSize: '2rem',
  },
  h3: {
    fontWeight: 600,
    fontSize: '1.75rem',
  },
  h4: {
    fontWeight: 600,
    fontSize: '1.5rem',
  },
  h5: {
    fontWeight: 600,
    fontSize: '1.25rem',
  },
  h6: {
    fontWeight: 600,
    fontSize: '1rem',
  },
  body1: {
    fontSize: '1rem',
    fontWeight: 400,
  },
  body2: {
    fontSize: '0.875rem',
    fontWeight: 400,
  },
  button: {
    fontWeight: 500,
    textTransform: 'none',
  },
};

// EdTech Color Palette
const edtechColors = {
  // Tech Teal (Main Brand) - #00BCD4
  primary: {
    main: '#00BCD4',
    light: '#4DD0E1',
    dark: '#0097A7',
    contrastText: '#FFFFFF',
  },
  // Aspiration Orange (Action) - #FF9800
  secondary: {
    main: '#FF9800',
    light: '#FFB74D',
    dark: '#F57C00',
    contrastText: '#FFFFFF',
  },
  // Success Green - #4CAF50
  success: {
    main: '#4CAF50',
    light: '#81C784',
    dark: '#388E3C',
    contrastText: '#FFFFFF',
  },
  // Warning Red - #F44336
  error: {
    main: '#F44336',
    light: '#E57373',
    dark: '#D32F2F',
    contrastText: '#FFFFFF',
  },
  warning: {
    main: '#FF9800',
    light: '#FFB74D',
    dark: '#F57C00',
    contrastText: '#FFFFFF',
  },
  info: {
    main: '#00BCD4',
    light: '#4DD0E1',
    dark: '#0097A7',
    contrastText: '#FFFFFF',
  },
  // White surfaces; subtle tint for card headers (contrast vs paper body)
  background: {
    default: '#FFFFFF',
    paper: '#FFFFFF',
    subtle: '#c7eff4',
  },
  text: {
    primary: 'rgba(0, 0, 0, 0.87)',
    secondary: 'rgba(0, 0, 0, 0.6)',
    disabled: 'rgba(0, 0, 0, 0.38)',
  },
  divider: 'rgba(0, 0, 0, 0.12)',
  action: {
    active: 'rgba(0, 0, 0, 0.54)',
    hover: 'rgba(0, 188, 212, 0.08)',
    selected: 'rgba(0, 188, 212, 0.16)',
    disabled: 'rgba(0, 0, 0, 0.26)',
    disabledBackground: 'rgba(0, 0, 0, 0.12)',
  },
};

const edtechTheme = createTheme({
  palette: {
    mode: 'light',
    ...edtechColors,
  },
  typography,
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 500,
        },
        contained: {
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
          },
        },
        containedPrimary: {
          backgroundColor: edtechColors.primary.main,
          '&:hover': {
            backgroundColor: edtechColors.primary.dark,
          },
        },
        containedSecondary: {
          backgroundColor: edtechColors.secondary.main,
          '&:hover': {
            backgroundColor: edtechColors.secondary.dark,
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: edtechColors.primary.main,
          color: edtechColors.primary.contrastText,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        colorSuccess: {
          backgroundColor: edtechColors.success.main,
          color: edtechColors.success.contrastText,
        },
        colorError: {
          backgroundColor: edtechColors.error.main,
          color: edtechColors.error.contrastText,
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          '& .MuiSelect-select': {
            padding: '14px 40px 14px 14px',
            textAlign: 'center',
            minHeight: '20px',
            lineHeight: '20px',
          },
          '& .MuiSelect-icon': {
            right: '12px',
            color: 'rgba(0, 0, 0, 0.54)',
          },
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: edtechColors.action.hover,
          },
          '&.Mui-selected': {
            backgroundColor: edtechColors.action.selected,
            '&:hover': {
              backgroundColor: edtechColors.action.selected,
            },
          },
        },
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            backgroundColor: edtechColors.background.paper,
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            },
            '&.Mui-focused': {
              boxShadow: `0 4px 12px rgba(0, 188, 212, 0.3)`,
            },
            '& .MuiSelect-select': {
              borderRadius: 8,
            },
          },
        },
      },
    },
    MuiMenu: {
      defaultProps: {
        disableScrollLock: true,
      },
      styleOverrides: {
        paper: {
          borderRadius: 8,
          boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
          border: '1px solid',
          borderColor: edtechColors.divider,
        },
      },
    },
    MuiPopover: {
      defaultProps: {
        disableScrollLock: true,
      },
    },
    MuiCardHeader: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.palette.background.subtle,
          borderBottom: `1px solid ${theme.palette.divider}`,
          '& .MuiCardHeader-title': {
            color: theme.palette.text.primary,
          },
        }),
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: ({ theme }) => ({
          backgroundColor: 'transparent',
          color: theme.palette.text.primary,
          fontWeight: 600,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }),
        body: {
          fontSize: '14px',
        },
      },
    },
  },
});

export { edtechTheme };
